import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PhoneInTalkOutlined,
  TimelineOutlined,
} from '@mui/icons-material';
import { salesActivityLogService } from '../../services/salesActivityLogService';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  ACTIVITY_TYPE_META,
  SalesCard,
  SalesEmptyState,
  SalesPageHeader,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesRowHoverSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const SalesActivityLog = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activityType, setActivityType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await salesActivityLogService.getAll({
        search: search || undefined,
        activity_type: activityType || undefined,
        limit: 100,
      });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [search, activityType]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const byType = { phone: 0, onsite_visit: 0, email: 0, other: 0 };
    rows.forEach((r) => {
      if (byType[r.activity_type] != null) byType[r.activity_type] += 1;
    });
    return byType;
  }, [rows]);

  const targetLabel = (row) => {
    if (row.lead_id) return `Lead #${row.lead_number} — ${row.lead_company_name || ''}`;
    if (row.customer_id) return `Customer #${row.customer_number} — ${row.customer_company_name || ''}`;
    return '—';
  };

  const openTarget = (row) => {
    if (row.lead_id) navigate(`/sales/leads/${row.lead_id}`);
    else if (row.customer_id) navigate(`/sales/customers/${row.customer_id}`);
  };

  return (
    <Box>
      <SalesPageHeader
        title="Activity Log"
        subtitle="A chronological record of your calls, visits, and follow-ups across leads and customers."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<PhoneInTalkOutlined />}
              onClick={() => navigate('/sales/activity/new')}
              sx={salesPrimaryButtonSx}
            >
              Log Call / Visit
            </Button>
          </>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {[
          { key: '', label: `All (${rows.length})` },
          { key: 'phone', label: `Phone (${counts.phone})` },
          { key: 'onsite_visit', label: `Visits (${counts.onsite_visit})` },
          { key: 'email', label: `Email (${counts.email})` },
        ].map((t) => (
          <Button
            key={t.key || 'all'}
            size="small"
            variant={activityType === t.key ? 'contained' : 'outlined'}
            onClick={() => setActivityType(t.key)}
            sx={{
              textTransform: 'none',
              borderRadius: 5,
              ...(activityType === t.key
                ? salesContainedChipSx()
                : { borderColor: SALES.line, color: SALES.ink }),
            }}
          >
            {t.label}
          </Button>
        ))}
      </Stack>

      <SalesCard sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search notes, contact, company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: SALES.muted, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
            >
              <MenuItem value="">All types</MenuItem>
              {Object.entries(ACTIVITY_TYPE_META).map(([key, meta]) => (
                <MenuItem key={key} value={key}>{meta.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </SalesCard>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <SalesEmptyState
          icon={<TimelineOutlined sx={{ fontSize: 42 }} />}
          title="No activity logged yet"
          description="Log a call or onsite visit and pick the lead or customer as you go — you can create a new lead in the same step."
          actionLabel="Log Call / Visit"
          onAction={() => navigate('/sales/activity/new')}
        />
      ) : (
        <TableContainer component={SalesCard}>
          <Table size="small">
            <TableHead sx={salesTableHeadSx}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Related To</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Next Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover sx={salesRowHoverSx} onClick={() => openTarget(row)}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {formatDateDisplay(row.activity_date)}
                  </TableCell>
                  <TableCell>
                    <SalesStatusChip status={row.activity_type} metaMap={ACTIVITY_TYPE_META} />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: SALES.ink }}>{targetLabel(row)}</Typography>
                  </TableCell>
                  <TableCell>{row.contact_name || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={row.notes}>{row.notes}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.next_action || '—'}
                    {row.next_action_date ? (
                      <Typography variant="caption" display="block" sx={{ color: SALES.muted }}>
                        {formatDateDisplay(row.next_action_date)}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

function salesContainedChipSx() {
  return {
    bgcolor: SALES.steel,
    color: '#fff',
    boxShadow: 'none',
    '&:hover': { bgcolor: '#155a8f' },
  };
}

export default SalesActivityLog;
